'use client';

import Markdown from 'react-markdown';

import { cn } from 'cn';
import rehypeExtLinks from 'rehype-external-links';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useTranslations } from 'use-intl';

import { useSetIframeUrl } from '@/shared-survey';
import { ReportProblem, rehypeIndent } from '@/shared-ui';

import { ProjectResponseAssetItem } from './project-response-asset-item';
import ProjectResponseMessageRendererAudio from './project-response-message-renderer-audio';
import { ensureTripleBackticks } from './project-response-message-renderers.helpers';
import { ProjectResponseComponentProps } from './project-response-renderer.type';

export default function ProjectResponseOpenEndRendererComponent({
  id,
  text,
  assets,
  audio,
  type,
  contentUrl,
  isInsightShare,
  isAiVoiceEnabled,
  shouldAnimate = false,
  isFollowUp = false,
  onSubmit,
  questionId,
}: ProjectResponseComponentProps) {
  const t = useTranslations('UserTest');

  const setIframeUrl = useSetIframeUrl();

  const handleClick = () => {
    if (isInsightShare && contentUrl) {
      window?.open(contentUrl, '_blank');
    } else if (contentUrl) {
      setIframeUrl(contentUrl);
    }
  };

  return (
    <div className='isolate float-right mb-6 flex w-full min-w-full flex-col items-start gap-1'>
      <div
        data-sender='assistant'
        data-test='open-end-renderer'
        className={cn(
          'prose max-w-full rounded-3xl rounded-bl-none p-6 text-left [font-family:var(--brand-font)] text-sm/tight break-normal text-(--brand-text-on-assistant-message) backdrop-blur-sm transition-opacity data-[optimistic=true]:opacity-90 data-[sender=assistant]:py-4',
          isFollowUp
            ? 'bg-(--brand-assistant-message-bg-secondary)'
            : 'bg-(--brand-assistant-message-bg)',
          shouldAnimate && 'animate-slide-in-from-bottom'
        )}
      >
        {audio && isAiVoiceEnabled && (
          <div className='my-2 flex w-full justify-start'>
            <ProjectResponseMessageRendererAudio audio={audio ?? undefined} />
          </div>
        )}

        {type === 'url' ? (
          <div className='mb-0 text-sm text-(--brand-text-on-assistant-message)'>
            <button
              type='button'
              className='font-inherit cursor-pointer border-0 bg-transparent p-0 text-inherit underline'
              onClick={handleClick}
            >
              {t('redirecting')}
            </button>
          </div>
        ) : (
          <div className='prose mb-0 text-(--brand-text-on-assistant-message) prose-headings:text-sm prose-p:text-sm'>
            <Markdown
              rehypePlugins={[
                rehypeRaw,
                rehypeIndent,
                [rehypeExtLinks, { target: '_blank' }],
              ]}
              remarkPlugins={[remarkGfm]}
              data-test='open-end-markdown'
            >
              {ensureTripleBackticks(text)}
            </Markdown>
          </div>
        )}

        {assets && assets?.length > 0 && assets[0]?.url && (
          <ProjectResponseAssetItem asset={assets[0]} />
        )}
      </div>
      {onSubmit && id && (
        <ReportProblem
          context={{ messageId: id, questionId: questionId ?? undefined }}
          onSubmit={onSubmit}
          variant={isFollowUp ? 'follow-up' : 'main'}
          className='self-start'
        />
      )}
    </div>
  );
}
